import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import SplashScreen from './shell/SplashScreen';
import { PermissionProvider } from './contexts/PermissionProvider';
import './styles.css';
import 'maplibre-gl/dist/maplibre-gl.css';
import { purgeLegacyDatasets } from './lib/idb';
import { useAgolAuthStore } from './stores/agolAuthStore';
import { usePackStore } from './lib/pack';
import { useTerrainAdvisorStore } from './stores/terrainAdvisorStore';
import { useAuthStore } from './stores/authStore';
import { usePilotStore } from './stores/pilotStore';
import { useWaypointStore } from './stores/waypointStore';
import { usePlanStore } from './stores/planStore';
import { useTrackStore } from './stores/trackStore';
import { useAccountContextStore } from './stores/accountContextStore';
import { installCrashLog } from './lib/crashLog';

// Install global error capture FIRST so we catch anything thrown by
// the other hydration calls below. Persists uncaught errors and
// unhandled rejections to localStorage so we can diagnose the
// 'black screen on track-log open' class of report.
installCrashLog();

// Drop legacy in-browser dataset caches that were causing iOS Safari OOMs.
// Fire-and-forget; ignored if IDB unavailable.
void purgeLegacyDatasets();

// ── Store hydration ────────────────────────────────────────────────────────────────
//
// Critical stores run synchronously before first paint — the map and flight
// plan must be available from the very first render tick.
//
// Non-critical stores are deferred via requestIdleCallback (Safari < 16
// fallback: setTimeout(0)) so they don't stall FCP. Components that read
// them will re-render when the hydration completes; nothing in the initial
// render depends on them being ready.

/** Schedule `fn` after the browser's first idle period (or on next tick in
 *  environments without requestIdleCallback such as Safari < 16). */
const deferHydration = (fn: () => void): void => {
  if (typeof window === 'undefined') { fn(); return; }
  if ('requestIdleCallback' in window) {
    (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void })
      .requestIdleCallback(fn, { timeout: 2000 });
  } else {
    setTimeout(fn, 0);
  }
};

// ─ Critical (synchronous) — needed before first render ───────────────────────
// Supabase auth: pulls session from localStorage, subscribes to changes,
// and kicks off the pilot-store sync. Must run first so RLS-gated fetches
// have a valid JWT.
void useAuthStore.getState().init();
// Active flight plan + in-progress track — both are localStorage-only and
// complete synchronously. The map renders them on first paint.
usePlanStore.getState().hydrate();
useTrackStore.getState().hydrate();

// ─ Non-critical (deferred) — not needed for initial map render ───────────
deferHydration(() => {
  // NIFC AGOL auth — only needed when the AGOL layer sheet is opened.
  void useAgolAuthStore.getState().hydrate();
});
deferHydration(() => {
  // Pack / IDB cache for plates and charts — relevant only after the user
  // taps "Pack for flight".
  usePackStore.getState().hydrate();
});
deferHydration(() => {
  // Terrain advisor — only active when the terrain layer is enabled.
  useTerrainAdvisorStore.getState().hydrate();
});
deferHydration(() => {
  // Pilot profile — needed for pilot detail pages, not the map itself.
  void usePilotStore.getState().hydrate();
});
deferHydration(() => {
  // User waypoints — shown on the map but populated via a background fetch;
  // deferring the hydration seed doesn’t affect the live-fetch path.
  void useWaypointStore.getState().hydrate();
});
deferHydration(() => {
  // Account context (personal vs org) — merges in server preference once
  // auth resolves; the default (personal) is fine for first paint.
  void useAccountContextStore.getState().hydrate();
});

// Expose key stores on window for headless E2E tests + console debugging.
// Harmless in prod — stores are app-level state, not secrets.
if (typeof window !== 'undefined') {
  (window as any).__fy = {
    ...((window as any).__fy ?? {}),
    planStore: usePlanStore,
    packStore: usePackStore,
    trackStore: useTrackStore,
  };
}

// Register the offline service worker. It intercepts tile + API GETs
// and stores them in named caches (`fyrefly-tiles-v1`, `fyrefly-api-v1`)
// so the EFB keeps working when cell service drops mid-flight. The
// Pack-for-flight button drives the initial cache population.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[fyrefly] service worker registration failed', err);
    });
  });
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <PermissionProvider>
        <App />
      </PermissionProvider>
    </BrowserRouter>
    <SplashScreen />
  </React.StrictMode>
);
